import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ICategoria } from 'modelos/src';
import {
  BOTONERA_SPECS,
  BotoneraTemplateId,
} from '../../../../auxiliares/plantillas/plantillas-spec';
import { ID_PLANTILLA_MANUAL } from '../../../../auxiliares/plantillas/plantilla-manual';
import { etiquetaCategoria } from '../cliente-form.model';

/** Lo que el diálogo necesita para poder decidir. Lo arma el padre (una sola vez). */
export interface DatosAgregarCategoria {
  /** Catálogo completo de categorías globales (con `idsBtns*` para contar botones). */
  categorias: ICategoria[];
  /** idCategoria → nombres de los clientes que la usan. */
  usoPorCategoria: Record<string, string[]>;
  /** Categorías que este cliente ya tiene (no se pueden volver a "usar", sí duplicar). */
  yaUsadas: string[];
}

/** Qué eligió el operador. */
export type ResultadoAgregarCategoria =
  | { modo: 'usar'; categoria: ICategoria }
  | { modo: 'duplicar'; categoria: ICategoria }
  | { modo: 'blanco'; preset: 'basica' | 'completa' };

/** Fila del listado, ya con todo lo que se muestra precalculado. */
interface FilaCategoria {
  categoria: ICategoria;
  etiqueta: string;
  botonera: string;
  cantidadBotones: number;
  /** Clientes que la usan (excluyendo a este). */
  clientes: string[];
  yaUsada: boolean;
}

/**
 * Único punto de entrada para sumar una categoría a un cliente. Reemplaza los tres caminos que hay
 * hoy (el select del wizard, el select de `categorias-cliente` y el "agregar categoría" del
 * provisionador) por una sola pantalla con las tres opciones reales:
 *
 *  - **Usar** una categoría global tal cual (queda compartida: se avisa con cuántos clientes).
 *  - **Duplicar** una existente (copia datos + botonera + los botones POR REFERENCIA; no crea
 *    botones nuevos). Es el caso más frecuente: un cliente nuevo es casi siempre copia de otro.
 *  - **En blanco**, con dos presets de cantidad de pantallas.
 */
@Component({
  selector: 'app-agregar-categoria-dialog',
  templateUrl: './agregar-categoria-dialog.component.html',
  styleUrls: ['./agregar-categoria-dialog.component.scss'],
  standalone: false,
})
export class AgregarCategoriaDialogComponent {
  private dialogRef =
    inject<
      MatDialogRef<AgregarCategoriaDialogComponent, ResultadoAgregarCategoria>
    >(MatDialogRef);
  public data = inject<DatosAgregarCategoria>(MAT_DIALOG_DATA);

  public busqueda = '';
  /** Ocultar las que ya tiene el cliente (por defecto no, para poder duplicarlas). */
  public soloDisponibles = false;

  private readonly filas: FilaCategoria[] = this.armarFilas();

  private armarFilas(): FilaCategoria[] {
    const yaUsadas = new Set(this.data.yaUsadas);
    return this.data.categorias.map((categoria) => {
      const id = categoria._id ?? '';
      const fam = categoria.inicioTemplate as BotoneraTemplateId | undefined;
      return {
        categoria,
        etiqueta: etiquetaCategoria(categoria),
        botonera:
          (fam as string) === ID_PLANTILLA_MANUAL
            ? 'Armada a mano'
            : fam && BOTONERA_SPECS[fam]
              ? BOTONERA_SPECS[fam].label
              : 'la del cliente',
        cantidadBotones:
          (categoria.idsBtnsPrincipales?.length ?? 0) +
          (categoria.idsBtnsSecundarios?.length ?? 0) +
          (categoria.idsOtrosBotones?.length ?? 0),
        clientes: this.data.usoPorCategoria[id] ?? [],
        yaUsada: yaUsadas.has(id),
      };
    });
  }

  get filasFiltradas(): FilaCategoria[] {
    const t = this.busqueda.trim().toLowerCase();
    return this.filas.filter((f) => {
      if (this.soloDisponibles && f.yaUsada) return false;
      if (!t) return true;
      return f.etiqueta.toLowerCase().includes(t);
    });
  }

  public usar(fila: FilaCategoria): void {
    this.dialogRef.close({ modo: 'usar', categoria: fila.categoria });
  }

  public duplicar(fila: FilaCategoria): void {
    this.dialogRef.close({ modo: 'duplicar', categoria: fila.categoria });
  }

  public enBlanco(preset: 'basica' | 'completa'): void {
    this.dialogRef.close({ modo: 'blanco', preset });
  }

  public cerrar(): void {
    this.dialogRef.close();
  }
}
